import { Badge, Chip, Tooltip } from '@mui/material';
import type { GridColDef } from '@mui/x-data-grid-premium';

import CommaSeparatedList from '~/components/common/CommaSeparatedList';
import {
  DISPUTE_STATUS_COLOR,
  DISPUTE_STATUS_LABEL,
  type DisputeStatus,
} from '~/components/disputes/types';
import CreditStatusChip from '~/components/invoices/CreditStatusChip';
import { ShowDocumentCell } from '~/components/rowPreview/ShowDocumentCell';
import { colDefTemplate } from '~/components/table/colDefTemplate';
import {
  basicNumberComparisonOperator,
  onlyContainsFilterOperator,
  onlyIsAnyOfFilterOperator,
  onOrBeforeOrOnOrAfterFilterOperator,
} from '~/components/table/filterOperators';
import { getApplyQuickFilterFn } from '~/components/table/helpers';
import { LiftAITextLink } from '~/components/utils/helpers';
import { isOriginalDocumentPage } from '~/data/schemas/attachment';
import type { Invoice, InvoiceWithPortfolio } from '~/data/schemas/invoice';
import {
  InvoiceKind,
  InvoiceRejectionCategoryOptions,
  invoiceRejectionCategoryToLabelMap,
  invoiceStatusOptions,
  invoiceStatusToColorMap,
  invoiceStatusToLabelMap,
  invoiceTypeOptions,
  isCredit,
} from '~/data/schemas/invoice';
import { LocaleTerms } from '~/hooks/useLocaleTerms';
import { formatCurrency } from '~/utils/format';
import {
  buildUrlForInvoices,
  buildUrlForProperty,
  buildUrlForPropertyTickets,
  buildUrlForTickets,
} from '~/utils/urlBuilder';

const DISPUTE_STATUS_FILTER_OPTIONS: { value: DisputeStatus | 'action-needed'; label: string }[] = [
  { value: 'action-needed', label: 'Action Needed' },
  { value: 'active', label: 'Active' },
  { value: 'awaiting-vendor', label: 'Awaiting Vendor' },
  { value: 'resolved', label: 'Resolved' },
  { value: 'withdrawn', label: 'Withdrawn' },
];

type GetSpendingTableColumns = {
  currencyCode: string;
  additionalColumns?: GridColDef<InvoiceWithPortfolio>[];
  propertyId?: string;
  canFilterProperty?: boolean;
  canLinkToProperty?: boolean;
  /**
   * Let the Class column be filtered to invoices or proposals. Off for the spending page,
   * where the view toggle already fixes the class; on where both classes are listed
   * together.
   */
  canFilterClass?: boolean;
  terms: LocaleTerms;
  showDisputeManagement?: boolean;
  onPreviewDocument?: (row: InvoiceWithPortfolio) => void;
};

const NumberCell = ({
  row,
  onPreviewDocument,
}: {
  row: InvoiceWithPortfolio;
  onPreviewDocument?: (row: InvoiceWithPortfolio) => void;
}) => {
  const showDocument =
    onPreviewDocument && row.attachments.some(isOriginalDocumentPage)
      ? () => onPreviewDocument(row)
      : undefined;

  return (
    <ShowDocumentCell onShowDocument={showDocument}>
      <LiftAITextLink label={row.number} style={{ cursor: 'pointer' }} />
    </ShowDocumentCell>
  );
};

const RelatedInvoicesCell = ({ row }: { row: InvoiceWithPortfolio }) => {
  const items = row.relatedInvoices.map((item, i) => {
    const url = buildUrlForInvoices({
      invoiceId: item.id,
      kind: item.kind,
    });
    return <LiftAITextLink key={i} label={item.number} href={url} />;
  });
  return <CommaSeparatedList items={items} />;
};

const RelatedTicketsCell = ({
  row,
  propertyId,
}: {
  row: InvoiceWithPortfolio;
  propertyId?: string;
}) => {
  const items = row.tickets.map((item, i) => {
    const url = propertyId
      ? buildUrlForPropertyTickets({
          propertyId,
          number: item.number,
          ticketId: item.id,
        })
      : buildUrlForTickets({
          number: item.number,
          ticketId: item.id,
        });
    return <LiftAITextLink key={i} label={item.number} href={url} />;
  });
  return <CommaSeparatedList items={items} />;
};

const PropertyCell = ({
  value,
  propertyId,
  canLinkToProperty,
}: {
  value: InvoiceWithPortfolio['property'] | null;
  propertyId?: string;
  canLinkToProperty?: boolean;
}) => {
  if (!value) {
    return '-';
  }

  return propertyId && canLinkToProperty ? (
    <LiftAITextLink label={value.name} href={buildUrlForProperty(propertyId)} />
  ) : (
    value.name
  );
};

export function getSpendingTableColumns({
  currencyCode,
  additionalColumns = [],
  propertyId,
  canFilterProperty = true,
  canLinkToProperty = true,
  canFilterClass = false,
  terms,
  showDisputeManagement = false,
  onPreviewDocument,
}: GetSpendingTableColumns) {
  const spendingTableCols = colDefTemplate<InvoiceWithPortfolio>({
    options: {
      autoWidth: false,
    },
    cols: [
      {
        field: 'kind',
        headerName: 'Class',
        type: 'singleSelect',
        valueOptions: [
          { value: InvoiceKind.Invoice, label: 'Invoice' },
          { value: InvoiceKind.Proposal, label: 'Proposal' },
          { value: InvoiceKind.Credit, label: 'Credit' },
        ],
        flex: 1,
        minWidth: 120,
        filterable: canFilterClass,
        filterOperators: onlyIsAnyOfFilterOperator,
        aggregable: false,
      },
      {
        field: 'number',
        headerName: 'Number',
        filterOperators: onlyContainsFilterOperator,
        sortable: false,
        aggregable: false,
        flex: 1,
        minWidth: 120,
        renderCell: ({ row }) => <NumberCell row={row} onPreviewDocument={onPreviewDocument} />,
      },
      {
        field: 'type',
        headerName: 'Type',
        type: 'singleSelect',
        aggregable: false,
        filterOperators: onlyIsAnyOfFilterOperator,
        flex: 1,
        minWidth: 120,
        valueOptions: invoiceTypeOptions.map(([key, value]) => ({
          value: key,
          label: value,
        })),
      },
      {
        field: 'date',
        headerName: 'Date',
        valueGetter: (value, row) => (row.date ? new Date(row.date) : null),
        valueFormatter: (value: string | null) =>
          value ? new Date(value).toLocaleDateString('EN-US') : '-',
        type: 'date',
        filterable: false,
        aggregable: false,
        flex: 1,
        minWidth: 110,
      },
      {
        field: 'dateStamped',
        headerName: 'Date Stamped',
        aggregable: false,
        filterOperators: onOrBeforeOrOnOrAfterFilterOperator,
        valueGetter: (value, row) => (row.dateStamped ? new Date(row.dateStamped) : null),
        valueFormatter: (value: string | null) =>
          value ? new Date(value).toLocaleDateString('EN-US') : '-',
        type: 'date',
        flex: 1,
        minWidth: 175,
      },
      {
        field: 'dateUploaded',
        headerName: 'Date Uploaded',
        aggregable: false,
        filterOperators: onOrBeforeOrOnOrAfterFilterOperator,
        valueGetter: (value, row) => (row.createdAt ? new Date(row.createdAt) : null),
        valueFormatter: (value: string | null) =>
          value ? new Date(value).toLocaleDateString('EN-US') : '-',
        type: 'date',
        flex: 1,
        minWidth: 175,
      },
      {
        field: 'status',
        headerName: 'Status',
        type: 'singleSelect',
        aggregable: false,
        filterOperators: onlyIsAnyOfFilterOperator,
        flex: 1,
        minWidth: 120,
        valueOptions: invoiceStatusOptions.map(([key, value]) => ({
          value: key,
          label: value,
        })),
        renderCell(params) {
          if (isCredit(params.row)) {
            return <CreditStatusChip credit={params.row} />;
          }
          const label = invoiceStatusToLabelMap.get(params.value);
          const color = invoiceStatusToColorMap.get(params.value)!;

          if (!label || !color) {
            return null;
          }

          return <Chip label={label} color={color} variant="outlined" />;
        },
      },
      {
        field: 'disputeStatus',
        headerName: 'Dispute',
        type: 'singleSelect',
        aggregable: false,
        filterOperators: onlyIsAnyOfFilterOperator,
        flex: 1,
        minWidth: 150,
        valueOptions: DISPUTE_STATUS_FILTER_OPTIONS,
        renderCell(params) {
          const disputeStatus = params.value as DisputeStatus | undefined;
          if (!disputeStatus || disputeStatus === 'none') {
            return null;
          }

          const needsAttention = params.row.disputeNeedsAttention === true;

          // Show "Action Needed" chip with badge when vendor has responded and action is needed
          if (needsAttention && disputeStatus === 'active') {
            return (
              <Tooltip title="Vendor responded - action needed">
                <Badge
                  color="error"
                  variant="dot"
                  overlap="rectangular"
                  sx={{
                    '& .MuiBadge-badge': {
                      right: -4,
                      top: 4,
                    },
                  }}
                >
                  <Chip label="Action Needed" color="primary" size="small" variant="outlined" />
                </Badge>
              </Tooltip>
            );
          }

          return (
            <Chip
              label={DISPUTE_STATUS_LABEL[disputeStatus]}
              color={DISPUTE_STATUS_COLOR[disputeStatus]}
              size="small"
              variant="outlined"
            />
          );
        },
      },
      {
        field: 'property',
        headerName: 'Property',
        nested: { query: 'property.name' },
        aggregable: false,
        filterOperators: onlyContainsFilterOperator,
        filterable: canFilterProperty,
        flex: 1,
        minWidth: 150,
        // The row value is the invoice's embedded property, not the full
        // property read; only `name` is used.
        valueFormatter: (value: Invoice['property'] | null) => value?.name ?? '-',
        renderCell: ({ value }) => (
          <PropertyCell
            value={value}
            propertyId={propertyId}
            canLinkToProperty={canLinkToProperty}
          />
        ),
        sortComparator: (prev, curr, ...rest) => (prev?.name ?? '').localeCompare(curr?.name ?? ''),
        getApplyQuickFilterFn(value, colDef, apiRef) {
          return getApplyQuickFilterFn(value, 'property.name');
        },
      },
      {
        field: 'tickets',
        headerName: 'Tickets',
        aggregable: false,
        filterOperators: onlyContainsFilterOperator,
        flex: 1,
        minWidth: 120,
        valueFormatter: (value: InvoiceWithPortfolio['tickets']) =>
          value.map((ticket: { number: string }) => ticket.number).join(', '),
        renderCell: ({ row }: { row: InvoiceWithPortfolio }) => (
          <RelatedTicketsCell row={row} propertyId={propertyId} />
        ),
      },
      {
        field: 'relatedInvoices',
        headerName: 'Related Invoices & Proposals',
        aggregable: false,
        filterOperators: onlyContainsFilterOperator,
        flex: 1,
        minWidth: 260,
        valueFormatter: (value: InvoiceWithPortfolio['relatedInvoices']) =>
          value.map((invoice: { number: string }) => invoice.number).join(', '),
        renderCell: ({ row }) => {
          return <RelatedInvoicesCell row={row} />;
        },
      },
      {
        field: 'portfolio',
        headerName: 'Portfolio',
        nested: { query: 'portfolio.name' },
        valueFormatter: (value: InvoiceWithPortfolio['portfolio']) => value?.name ?? '-',
        renderCell: ({ value }) => value?.name ?? '-',
        sortComparator: (prev, curr, ...rest) => (prev?.name ?? '').localeCompare(curr?.name ?? ''),
        getApplyQuickFilterFn(value, colDef, apiRef) {
          return getApplyQuickFilterFn(value, 'portfolio.name');
        },
        filterable: false,
        aggregable: false,
        flex: 1,
        minWidth: 130,
      },
      {
        field: 'region',
        headerName: 'Region',
        filterable: false,
        sortable: false,
        aggregable: false,
        flex: 1,
        minWidth: 100,
      },
      {
        field: 'serviceProvider',
        headerName: 'Provider',
        nested: { query: 'serviceProvider.name' },
        filterOperators: onlyContainsFilterOperator,
        filterable: false,
        sortable: false,
        aggregable: false,
        flex: 1,
        minWidth: 120,
        valueFormatter: (value: Invoice['serviceProvider']) => value?.name,
        getApplyQuickFilterFn(value, colDef, apiRef) {
          return getApplyQuickFilterFn(value, 'serviceProvider.name');
        },
        sortComparator: (prev, curr, ...rest) => (prev?.name ?? '').localeCompare(curr?.name ?? ''),
      },
      {
        field: 'carsServiced',
        headerName: terms.vehiclesServiced,
        filterable: false,
        sortable: false,
        aggregable: false,
        flex: 1,
        minWidth: 150,
        valueFormatter: (value: InvoiceWithPortfolio['carsServiced']) =>
          value.map((car: { name: string }) => car.name).join(', '),
        renderCell: ({ row }) => {
          const items = row.carsServiced.map((item, i) => <span key={i}>{item.name}</span>);
          return <CommaSeparatedList items={items} />;
        },
      },
      {
        field: 'description',
        headerName: 'Description',
        filterable: false,
        sortable: false,
        aggregable: false,
        flex: 1,
        minWidth: 150,
      },
      {
        field: 'consultant',
        headerName: 'Consultant',
        filterable: false,
        sortable: false,
        aggregable: false,
        nested: { query: 'consultant.fullName' },
        flex: 1,
        minWidth: 120,
        valueFormatter: (value: InvoiceWithPortfolio['consultant']) => {
          return value?.fullName ?? '-';
        },
        getApplyQuickFilterFn(value, colDef, apiRef) {
          return getApplyQuickFilterFn(value, 'consultant.fullName');
        },
        sortComparator: (prev, curr, ...rest) => {
          return (prev?.fullName ?? '').localeCompare(curr?.fullName ?? '');
        },
      },
      {
        field: 'proposedAmount',
        headerName: 'Invoice Amount',
        filterOperators: basicNumberComparisonOperator,
        aggregable: false,
        type: 'number',
        flex: 1,
        minWidth: 175,
        // A credit is a deduction, unless it's kept on file only.
        valueGetter: (value: InvoiceWithPortfolio['proposedAmount'], row) =>
          isCredit(row) && !row.keepOnFileOnly && value != null ? -value : value,
        valueFormatter: (value: InvoiceWithPortfolio['proposedAmount']) =>
          value !== null ? formatCurrency({ value: value ?? 0, currencyCode }) : null,
      },
      {
        field: 'reviewedAmount',
        headerName: 'After Review',
        filterOperators: basicNumberComparisonOperator,
        aggregable: false,
        type: 'number',
        flex: 1,
        minWidth: 160,
        valueGetter: (_, row) => {
          if (isCredit(row)) {
            return null;
          }
          if (row.status === 'approved') {
            return row.proposedAmount;
          }

          return row.reviewedAmount;
        },
        valueFormatter: (value: InvoiceWithPortfolio['reviewedAmount']) =>
          value !== null ? formatCurrency({ value: value ?? 0, currencyCode }) : null,
      },
      {
        field: 'savings',
        headerName: 'Savings',
        filterOperators: basicNumberComparisonOperator,
        aggregable: false,
        type: 'number',
        flex: 1,
        minWidth: 125,
        valueGetter: (value, row) => row.savings,
        valueFormatter: (value: number | null) =>
          value !== null ? formatCurrency({ value, currencyCode }) : null,
      },
      {
        field: 'projectedSavings',
        headerName: 'Projected Savings',
        filterable: false,
        aggregable: false,
        type: 'number',
        flex: 1,
        minWidth: 160,
        valueGetter: (value, row) => row.projectedSavings ?? null,
        valueFormatter: (value: number | null) =>
          value != null && value > 0 ? formatCurrency({ value, currencyCode }) : '-',
      },
      {
        field: 'rejectionCategory',
        headerName: 'Rejection Category',
        type: 'singleSelect',
        filterOperators: onlyIsAnyOfFilterOperator,
        valueOptions: InvoiceRejectionCategoryOptions.map(([key, value]) => ({
          value: key,
          label: value,
        })),
        renderCell: ({ row }) => {
          const items = row.rejectionCategory?.map((key) => (
            <span key={key}>{invoiceRejectionCategoryToLabelMap.get(key) ?? key}</span>
          ));
          return <CommaSeparatedList items={items} />;
        },
        sortable: false,
        aggregable: false,
        flex: 1,
        minWidth: 290,
      },
      {
        field: 'reason',
        headerName: 'Reason',
        filterable: false,
        sortable: false,
        aggregable: false,
        flex: 1,
        minWidth: 120,
      },
      ...additionalColumns,
    ],
  });

  if (!showDisputeManagement) {
    return spendingTableCols.filter((col) => col.field !== 'disputeStatus');
  }

  return spendingTableCols;
}
